import { FC, useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import plus from '../../assets/icons/plus.svg'
import { CardCopy } from './CardCopy/CardCopy'
import { Colour } from '../constants'
import { AnimationTrigger } from '../Sections/PageHero'
import { CardCopyPosition, CardProps } from '../CardPanel/CardPanelTypes'
import { CardTakeover } from './CardTakeover/CardTakeover'
import { IconographyButton } from '../IconographyButton/IconographyButton'
import { useInitialScroll } from '@/hooks/useInitialScroll'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { useIsBlogPage } from '@/hooks/useIsBlogPage'
import { CardImage } from './CardImage/CardImage'
import { InlineVideo } from '../InlineVideo/InlineVideo'
import { LottieAnimation } from '../LottieAnimation/LottieAnimation'
import { CardBackgroundImage } from '../CardBackgroundImage/CardBackgroundImage'
import { Video } from '../Video/Video'
import styles from './Card.module.scss'

export const Card: FC<CardProps> = ({
  fields,
  index = 0,
  rowLength = 1,
  animateCardPosition = false,
}) => {
  const {
    title,
    number,
    cardTakeover,
    urlDisplay,
    url,
    subtitle,
    iconImage,
    bodyCopy,
    backgroundColour = Colour.warmStone,
    image,
    lottieFile,
    inlineVideo,
    video,
    videoPoster,
    mobileVideo,
    mobileVideoPoster,
    loopAnimation = false,
    animationTrigger,
    imageFit,
    hideImageOnMobile,
    backgroundImage,
    padCopy = true,
    increasedPadding = false,
    copyPosition = CardCopyPosition.TOP,
    richText,
    richTextListMarkerStyle,
    showOpenAccountButton,
    openAccountButtonText,
    primaryCtaText,
    primaryCtaUrl,
    inCarousel,
    reduceImageSize = false,
    reverseOrder = false,
    downloadButtonText,
    downloadableAsset,
    takeoverButtonColour = Colour.fuschiaPunch,
  } = fields

  const [showCardTakeover, setShowCardTakeOver] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const [clickableCard, setClickableCard] = useState(false)
  const [cardClicked, setCardClicked] = useState(false)

  const MAX_CTAS_FOR_CLICKABLE_CARD = 1
  const shouldWatchScrollIntoView =
    (lottieFile || inlineVideo) &&
    animationTrigger === AnimationTrigger.onScrollIntoView
  const shouldWatchOnScroll =
    (lottieFile || inlineVideo) &&
    animationTrigger === AnimationTrigger.onScroll
  const triggerInternalAnimationOnIntersect = useIntersectionObserver(
    cardRef,
    shouldWatchScrollIntoView,
  )
  const triggerInternalAnimationOnScroll = useInitialScroll(shouldWatchOnScroll)
  const triggerPositionAnimation = useIntersectionObserver(
    cardRef,
    animateCardPosition,
    0.8,
  )

  useEffect(() => {
    if (cardRef.current) {
      setClickableCard(
        cardRef.current.querySelectorAll('a, button').length ===
          MAX_CTAS_FOR_CLICKABLE_CARD,
      )
    }
  }, [])

  const toggleCardTakeover = () => {
    setShowCardTakeOver(!showCardTakeover)
  }

  const isSingleRow = () => rowLength === 1
  const reducedHeaders = () => rowLength === 3

  const isBlog = useIsBlogPage()
  const hasContent = () =>
    title ||
    number ||
    subtitle ||
    bodyCopy ||
    richText ||
    (primaryCtaText && primaryCtaUrl) ||
    showOpenAccountButton ||
    (url && urlDisplay) ||
    (downloadButtonText && downloadableAsset)

  const handleCardClick = () => {
    if (clickableCard && cardRef.current && !showCardTakeover) {
      const target = cardRef.current.querySelectorAll(
        'a, button',
      )[0] as HTMLElement

      target?.click()
      setCardClicked(true)
    }
  }

  useEffect(() => {
    const setClicked = () => setCardClicked(false)

    window.addEventListener('resize', setClicked)

    return () => window.removeEventListener('resize', setClicked)
  }, [])

  return (
    <>
      <div
        ref={cardRef}
        onClick={handleCardClick}
        className={cx(styles.card, styles[backgroundColour], {
          [styles.triggerPositionAnimation]: triggerPositionAnimation,
          [styles.animateCardPosition]: animateCardPosition,
          [styles.delayTransition1]: index === 1,
          [styles.delayTransition2]: index === 2,
          [styles.blog]: isBlog,
          [styles.reverseOrder]: reverseOrder,
          [styles.hasBackgroundImage]: Boolean(backgroundImage),
          [styles.hasContent]: hasContent(),
          [styles.inCarousel]: inCarousel,
          [styles.clickable]: clickableCard && !showCardTakeover,
          [styles.hasVideo]: Boolean(video || mobileVideo),
        })}
      >
        <div
          className={cx(styles.inner, {
            [styles.singleCardRow]: isSingleRow(),
            [styles.reverseOrder]: reverseOrder,
            [styles.multiCardRow]: !isSingleRow(),
          })}
        >
          {hasContent() && (
            <CardCopy
              className={cx(styles.cardCopy, {
                [styles.reduceImageSize]: reduceImageSize,
              })}
              hasBackground={Boolean(backgroundImage)}
              hideImageOnMobile={hideImageOnMobile}
              padCopy={padCopy}
              increasedPadding={increasedPadding}
              copyPosition={copyPosition}
              title={title}
              number={number}
              subtitle={subtitle}
              iconImage={iconImage}
              bodyCopy={bodyCopy}
              url={url}
              urlDisplay={urlDisplay}
              richText={richText}
              richTextListMarkerStyle={richTextListMarkerStyle}
              showOpenAccountButton={showOpenAccountButton}
              openAccountButtonText={openAccountButtonText}
              primaryCtaUrl={primaryCtaUrl}
              primaryCtaText={primaryCtaText}
              reducedHeaders={reducedHeaders()}
              backgroundColour={backgroundColour}
              hasImage={Boolean(image || lottieFile)}
              singleCardRow={isSingleRow()}
              reverseOrder={reverseOrder}
              downloadButtonText={downloadButtonText}
              downloadableAsset={downloadableAsset}
            />
          )}
          {image && (
            <CardImage
              className={cx(styles.illustration, styles.staticImage, {
                [styles.triggerPositionAnimation]: triggerPositionAnimation,
                [styles.animateCardPosition]: animateCardPosition,
                [styles.reverseOrder]: reverseOrder,
                [styles.delayTransition1]: index === 1,
                [styles.delayTransition2]: index === 2,
              })}
              imageFit={imageFit}
              image={image}
              hideImageOnMobile={hideImageOnMobile}
            />
          )}
          {lottieFile && (
            <LottieAnimation
              loop={loopAnimation}
              className={styles.illustration}
              play={
                triggerInternalAnimationOnIntersect ||
                triggerInternalAnimationOnScroll
              }
              path={lottieFile.fields.file.url}
            />
          )}
          {inlineVideo && (
            <InlineVideo
              src={inlineVideo.fields.file.url}
              play={
                triggerInternalAnimationOnIntersect ||
                triggerInternalAnimationOnScroll
              }
              loop={loopAnimation}
            />
          )}

          {video && (
            <Video
              video={video}
              videoPoster={videoPoster}
              mobileVideo={mobileVideo}
              mobileVideoPoster={mobileVideoPoster}
              play={cardClicked}
            />
          )}
        </div>

        {cardTakeover && cardTakeover?.fields && (
          <div
            className={cx(styles.cta, styles.cardTakeover, {
              [styles.shown]: showCardTakeover,
            })}
          >
            <IconographyButton
              colour={takeoverButtonColour}
              className={styles.cardButton}
              icon={plus}
              value={showCardTakeover ? 'Close' : 'Open'}
              disabled={false}
              handler={toggleCardTakeover}
            />
          </div>
        )}

        {cardTakeover && (
          <CardTakeover
            toggleCardTakeover={toggleCardTakeover}
            shown={showCardTakeover}
            cardTakeover={cardTakeover}
            hideImageOnMobile={hideImageOnMobile}
            singleCardRow={isSingleRow()}
          />
        )}
        {backgroundImage && <CardBackgroundImage image={backgroundImage} />}
      </div>
    </>
  )
}
